import React, { createRef, type SyntheticEvent, type MouseEvent, type KeyboardEvent } from 'react';
import { SkeletonItem } from '@atlaskit/side-navigation';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { MenuItem } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/menu-item/main.tsx';
import {
	matchPartialUrlPathname,
	matchUrlPathname,
} from '@atlassian/jira-navigation-apps-sidebar-common/src/utils/url-matchers/index.tsx';
import { useIsJCSProjectType } from '@atlassian/jira-servicedesk-customer-service-common/src/utils/use-is-jcs/index.tsx';
import { useProject } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/project/index.tsx';
import { useIsAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-admin/index.tsx';
import { SETTINGS_IDS } from '../../../constants.tsx';
import type { SettingFeatures } from '../../../types/index.tsx';
import { getSpaUrlForClassicSettings } from '../../../utils/servicedesk-spa-url-classic-settings/index.tsx';
import messages from './messages.tsx';

type RequestsManagementMenuProps = {
	projectKey: string;
	features: SettingFeatures | null;
	onHandleLegacySPAClick: (
		e:
			| SyntheticEvent
			| MouseEvent<HTMLAnchorElement>
			| KeyboardEvent<HTMLAnchorElement>
			| MouseEvent<EventTarget>
			| KeyboardEvent<EventTarget>,
	) => void;
};

export const RequestsManagementMenu = ({
	projectKey,
	features,
	onHandleLegacySPAClick,
}: RequestsManagementMenuProps) => {
	const { formatMessage } = useIntl();
	const isAdmin = useIsAdmin();
	const { data: project } = useProject();
	const isJCS = useIsJCSProjectType(project?.type);

	if (features == null || projectKey == null) return <SkeletonItem />;
	const issueLayoutMessage = fg('jira-issue-terminology-update-m4')
		? messages.issueLayoutIssueTerminologyRefreshM4
		: messages.issueLayout;
	return (
		<>
			{(!fg('jcs_master_flag') || features.hasForms) && (
				<MenuItem
					analytics={{ itemId: SETTINGS_IDS.FORMS }}
					href={getSpaUrlForClassicSettings('forms', projectKey)}
					selectedOn={matchUrlPathname()}
					ref={createRef()}
				>
					{formatMessage(messages.forms)}
				</MenuItem>
			)}
			{features.hasWorkflows && (
				<>
					<MenuItem
						analytics={{
							itemId: SETTINGS_IDS.WORKFLOWS,
						}}
						ref={createRef()}
						href={
							!isAdmin
								? `/jira/servicedesk/projects/${projectKey}/settings/workflows`
								: `/plugins/servlet/project-config/${projectKey}/workflows`
						}
						selectedOn={matchUrlPathname()}
					>
						{formatMessage(messages.workflows)}
					</MenuItem>
				</>
			)}
			{features.hasSla && (
				<MenuItem
					analytics={{
						itemId: SETTINGS_IDS.SLAS,
					}}
					href={getSpaUrlForClassicSettings('sla', projectKey)}
					onClick={__SPA__ ? undefined : onHandleLegacySPAClick}
					selectedOn={matchPartialUrlPathname()}
					ref={createRef()}
				>
					{formatMessage(messages.sla)}
				</MenuItem>
			)}
			<MenuItem
				analytics={{
					itemId: SETTINGS_IDS.SATISFACTION_SETTINGS,
				}}
				href={getSpaUrlForClassicSettings('feedback-settings', projectKey)}
				selectedOn={matchUrlPathname()}
				ref={createRef()}
			>
				{formatMessage(messages.customerSatisfaction)}
			</MenuItem>
			{features.hasIssueTypes && (
				<MenuItem
					analytics={{ itemId: SETTINGS_IDS.ISSUE_TYPES }}
					href={`/plugins/servlet/project-config/${projectKey}/issuetypes`}
					selectedOn={matchPartialUrlPathname()}
					ref={createRef()}
				>
					{formatMessage(
						fg('jira-issue-terminology-update-m4')
							? messages.issueTypesIssueTerminologyRefreshM4
							: messages.issueTypes,
					)}
				</MenuItem>
			)}
			{isJCS && features.hasIssueLayout && (
				<MenuItem
					analytics={{ itemId: SETTINGS_IDS.ISSUE_LAYOUT }}
					selectedOn={matchUrlPathname()}
					href={`/plugins/servlet/project-config/${projectKey}/issuelayout`}
					ref={createRef()}
				>
					{formatMessage(
						fg('jira-issue-terminology-update-m4')
							? messages.issueLayoutIssueTerminologyRefreshM4
							: issueLayoutMessage,
					)}
				</MenuItem>
			)}
			{features.hasFields && (
				<MenuItem
					analytics={{
						itemId: SETTINGS_IDS.FIELDS,
					}}
					href={`/plugins/servlet/project-config/${projectKey}/fields`}
					selectedOn={matchUrlPathname()}
					ref={createRef()}
				>
					{formatMessage(messages.fields)}
				</MenuItem>
			)}
			{features.hasWorkflows && (
				<MenuItem
					analytics={{
						itemId: SETTINGS_IDS.SCREENS,
					}}
					href={`/plugins/servlet/project-config/${projectKey}/screens`}
					selectedOn={matchUrlPathname()}
					ref={createRef()}
				>
					{formatMessage(messages.screens)}
				</MenuItem>
			)}
		</>
	);
};
